import React, { useLayoutEffect, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaseStudyHeader } from '../components/CaseStudyHeader';
import { Footer } from '../components/Footer';
import { CustomCursor } from '../components/CustomCursor';
import { CaseStudyMobileHeader } from '../components/CaseStudyMobileHeader';
import { FloatingButtons } from '../components/FloatingButtons';
import { ChatDrawer } from '../components/ChatDrawer';
import { ReadingProgress } from '../components/ReadingProgress';
import { MobileBottomCTA } from '../components/MobileBottomCTA';
import { usePortfolio } from '../context/PortfolioContext';
import { useCaseStudyNavigation } from '../hooks/useCaseStudyNavigation';
import { useScrollAnimation } from '../utils/AnimationObserver';
import { Brain, ArrowLeft } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
const fadeUpVariant = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
function VimeoEmbed({
  videoId,
  caption,
  aspect = 56.25
}: {
  videoId: string;
  caption?: string;
  aspect?: number;
}) {
  return <div className="bg-white rounded-2xl overflow-hidden border border-slate-200" data-id="element-612">
      <div style={{
      padding: `${aspect}% 0 0 0`,
      position: 'relative'
    }} data-id="element-613">
        <iframe src={`https://player.vimeo.com/video/${videoId}?badge=0&autopause=0&player_id=0&app_id=58479`} frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%'
      }} title={caption || 'Video'} data-id="element-614" />
      </div>
      {caption && <p className="text-xs uppercase tracking-[0.2em] text-slate-500 px-6 py-4 text-center bg-slate-50 border-t border-slate-100" style={{
      fontFamily: "'Ronzino', sans-serif"
    }} data-id="element-615">
          {caption}
        </p>}
    </div>;
}
function ProofImage({
  src,
  alt,
  caption
}: {
  src: string;
  alt: string;
  caption?: string;
}) {
  return <div className="bg-slate-50 rounded-2xl overflow-hidden border border-slate-200" data-id="element-616">
      <img src={src} alt={alt} loading="lazy" decoding="async" className="w-full h-auto" data-id="element-617" />
      {caption && <p className="text-xs uppercase tracking-[0.2em] text-slate-500 px-6 py-4 text-center bg-slate-50 border-t border-slate-100" style={{
      fontFamily: "'Ronzino', sans-serif"
    }} data-id="element-618">
          {caption}
        </p>}
    </div>;
}
const processSteps = [{
  number: '01',
  title: 'Contextual inquiry',
  desc: '16 interviews across 7 colleges over two weeks. Faculty + admins, in their workflows, with their actual CVs.'
}, {
  number: '02',
  title: 'Affinity mapping',
  desc: 'Tagged every painpoint. The pattern surfaced: trust mattered more than speed.'
}, {
  number: '03',
  title: 'Rapid prototyping',
  desc: 'AI-assisted wireframing to test multiple concepts in parallel. Three live iterations with stakeholders, each reviewed in the same week.'
}, {
  number: '04',
  title: 'AI parser POC',
  desc: 'Worked alongside Python engineers to build a working CV parser. Wrote prompt logic, tested against 36-page CVs, tuned for accuracy.'
}, {
  number: '05',
  title: 'Stakeholder validation',
  desc: 'Recurring sessions with the provost\u2019s office. Decisions made in the room, signed off as we went.'
}, {
  number: '06',
  title: 'Pilot handoff',
  desc: 'Documented the methodology and handed the working prototype to Amazon for their 2026 rollout pilot.'
}];
export function CaseStudyCaseWestern({
  isOverlay = false
}: {
  isOverlay?: boolean;
}) {
  const navigate = useNavigate();
  const {
    viewPreference
  } = usePortfolio();
  useCaseStudyNavigation();
  useScrollAnimation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);
  return <>
      <ReadingProgress color="#003071" data-id="element-619" />
      {!isOverlay && <CustomCursor data-id="element-620" />}

      <div className="flex min-h-screen bg-white text-slate-900" data-id="element-621">
        <div className="flex-1 min-w-0 flex flex-col relative pb-20 lg:pb-0" data-id="element-622">
          {!isOverlay && <CaseStudyHeader data-id="element-623" />}
          {!isOverlay && <CaseStudyMobileHeader data-id="element-624" />}

          <main className="flex-1 w-full pb-32" data-id="element-625">
            {/* HEADER BLOCK */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="pt-32 sm:pt-40 pb-16 sm:pb-24 px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full" data-id="element-626">
              <button type="button" onClick={() => navigate(viewPreference === 'ai' ? '/portfolio/ai' : '/portfolio/traditional')} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-slate-900 hover:text-slate-500 transition-colors mb-10 group" style={{
              fontFamily: "'Ronzino', sans-serif"
            }} data-id="element-627">
                <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" data-id="element-628" />
                Back to home
              </button>

              <div className="mb-8" data-id="element-629">
                <Brain className="w-8 h-8 text-[#003071]" weight="light" data-id="element-630" />
              </div>

              <h1 className="text-3xl sm:text-5xl md:text-6xl font-light text-[#003071] leading-[1.1] tracking-tight mb-4" style={{
              fontFamily: "'Gefika', sans-serif"
            }} data-id="element-631">
                Faculty Information System
              </h1>

              <p className="text-lg sm:text-xl text-slate-500 font-light mb-8" style={{
              fontFamily: "'Gefika', sans-serif"
            }} data-id="element-632">
                Cutting hours of faculty CV review down to seconds with AI.
              </p>

              <p className="text-base sm:text-lg text-slate-700 leading-relaxed max-w-3xl" style={{
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }} data-id="element-633">
                I led the design of an AI-driven faculty data retrieval app at
                Case Western Reserve University. The tool ingests CVs, parses
                publications, grants, and credentials, and surfaces them
                instantly to administrators. What used to be a manual,
                spreadsheet-heavy review became a glanceable interface that kept
                humans in the loop.
              </p>
            </motion.section>

            {/* HERO IMAGE CARD */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full mb-16 sm:mb-24" data-id="element-634">
              <div className="bg-slate-100 rounded-2xl py-10 sm:py-14 md:py-16 px-6 sm:px-10 md:px-14 flex items-center justify-center" data-id="element-635">
                <img src="https://cdn.magicpatterns.com/uploads/r6QmYhNhTgdAoSYhnocL3u/CWRU_COMPV.png" alt="Faculty Information System Interface" className="w-full max-w-none object-contain drop-shadow-xl" data-id="element-636" />
              </div>
            </motion.section>

            {/* META STRIP */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full mb-24 sm:mb-32" data-id="element-637">
              <div className="border-t border-b border-slate-200 py-10" data-id="element-638">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-8 sm:gap-12 mb-12" data-id="element-639">
                  <div data-id="element-640">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#003071] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-641">
                      Product
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-642">
                      Faculty Information System
                    </div>
                  </div>
                  <div data-id="element-643">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#003071] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-644">
                      My role
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-645">
                      Lead Product Designer
                    </div>
                  </div>
                  <div data-id="element-646">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#003071] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-647">
                      Timeline
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-648">
                      Jan 2025 to Jun 2025
                    </div>
                  </div>
                  <div data-id="element-649">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#003071] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-650">
                      Skills
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-651">
                      Product design, AI integration, User research, Prototyping
                    </div>
                  </div>
                </div>
                <div className="pt-10 border-t border-slate-200" data-id="element-652">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#003071] mb-2" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-653">
                    Team
                  </div>
                  <div className="text-base font-medium text-slate-900" style={{
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }} data-id="element-654">
                    Mateo Cohen (Design Lead), CWRU Innovation Group,
                    Engineering Partners
                  </div>
                </div>
              </div>
            </motion.section>

            {/* QUOTE-OVERLAY IMAGE CARD */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full mb-24 sm:mb-32" data-id="element-655">
              <div className="relative rounded-2xl min-h-[280px] sm:h-[480px] sm:aspect-auto overflow-hidden flex items-center justify-center p-6 sm:p-16" data-id="element-656">
                <img src="https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=1600&q=80" alt="Faculty reviewing a printed CV at a desk" className="absolute inset-0 w-full h-full object-cover" data-id="element-657" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/40 to-slate-900/20" data-id="element-658"></div>

                <div className="relative z-10 text-center max-w-3xl mx-auto" data-id="element-659">
                  <p className="text-lg sm:text-3xl md:text-4xl font-light italic text-white leading-snug" style={{
                  fontFamily: "'Gefika', sans-serif"
                }} data-id="element-660">
                    "Reviewing a single CV could eat 30 minutes I didn't have."
                  </p>
                  <div className="text-xs tracking-[0.2em] uppercase text-white/70 mt-6" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-661">
                    Faculty admin, user interview
                  </div>
                </div>
              </div>
            </motion.section>

            {/* CONTENT SECTIONS */}
            <section className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full space-y-24 sm:space-y-32" data-id="element-662">
              {/* Section A: Problem */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-663">
                <div className="flex items-center gap-3 mb-4" data-id="element-664">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#003071'
                }} data-id="element-665">
                    01
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#003071'
                }} aria-hidden="true" data-id="element-666" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#003071] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-667">
                  Problem: The hidden cost of manual CV review
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-6 sm:mb-8" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-668">
                  CWRU administrators were spending roughly 30 minutes per
                  faculty CV. Reading inconsistent formats, hand-extracting
                  publications, grants, and credentials into spreadsheets. With
                  200+ faculty per cycle, that&rsquo;s 100+ hours of manual work
                  blocking grant timelines, accreditation, and internal reviews.
                </p>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-6 sm:mb-8" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-669">
                  And it wasn&rsquo;t work the institution could simply hire its
                  way out of. Faculty CVs aren&rsquo;t resumes. They&rsquo;re
                  20- to 40-page artifacts where formatting varies by
                  discipline, the same publication can appear three different
                  ways across two pages, and missing a single grant can hold up
                  an entire review. A generic OCR pipeline would surface text.
                  It wouldn&rsquo;t surface the right text in the right
                  structure, and it certainly wouldn&rsquo;t earn the trust of
                  the people signing off on the data.
                </p>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-8" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-670">
                  It wasn&rsquo;t a reading problem. It was a data-extraction
                  problem dressed up as a reading problem.
                </p>

                <div className="bg-slate-100 rounded-lg p-6 sm:p-8 my-10 overflow-x-auto" data-id="element-671">
                  <div className="text-xl sm:text-2xl font-light text-[#003071] sm:whitespace-nowrap" style={{
                  fontFamily: "'Gefika', sans-serif"
                }} data-id="element-672">
                    20- to 40-page CVs. 7 colleges. No standardization.
                  </div>
                </div>

                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-6" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-673">
                  And the legacy interface they were stuck with looked like
                  this:
                </p>

                <ProofImage src="https://uploadthingy.s3.us-west-1.amazonaws.com/vRuKFVtSu1k26mpfBtXnY7/Before.png" alt="Legacy Faculty Information System interface before redesign" caption="Before: the legacy FIS interface" data-id="element-674" />
              </motion.div>

              {/* Section B: Research */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-675">
                <div className="flex items-center gap-3 mb-4" data-id="element-676">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#003071'
                }} data-id="element-677">
                    02
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#003071'
                }} aria-hidden="true" data-id="element-678" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#003071] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-679">
                  Research: 16 interviews in two weeks
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-8" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-680">
                  I ran contextual inquiry with 16 faculty members and
                  administrators across 7 colleges, then affinity-mapped every
                  painpoint we surfaced. The pattern was clear: trust mattered
                  more than speed. Admins didn&rsquo;t want a black-box parser.
                  They wanted a tool that did the boring 80% and let them
                  sanity-check the rest.
                </p>

                <VimeoEmbed videoId="1142194486" caption="User interviews capturing sentiment and pain points" data-id="element-681" />
              </motion.div>

              {/* Process Timeline */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-682">
                <div className="flex items-center gap-3 mb-4" data-id="element-683">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#003071'
                }} data-id="element-684">
                    03
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#003071'
                }} aria-hidden="true" data-id="element-685" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#003071] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-686">
                  Process: Six steps, one shared definition of done
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-10" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-687">
                  Six months of work, telescoped into the six decisions that
                  actually moved the project forward.
                </p>

                <div data-id="element-688">
                  {processSteps.map((step, idx) => <div key={step.number} className="relative flex gap-5 sm:gap-8 pb-12 sm:pb-16 last:pb-0" data-id="element-689">
                      {idx !== processSteps.length - 1 && <div className="absolute left-5 sm:left-6 top-10 sm:top-12 bottom-0 w-px bg-slate-200" aria-hidden="true" data-id="element-690" />}
                      {/* Number */}
                      <div className="relative z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white border border-slate-300 flex items-center justify-center flex-shrink-0" data-id="element-691">
                        <span className="text-xs sm:text-sm font-medium text-slate-900" style={{
                      fontFamily: "'Ronzino', sans-serif"
                    }} data-id="element-692">
                          {step.number}
                        </span>
                      </div>
                      {/* Content */}
                      <div className="flex-1 pt-1.5 sm:pt-2.5" data-id="element-693">
                        <h4 className="text-lg sm:text-xl font-medium text-[#003071] mb-2" style={{
                      fontFamily: "'Gefika', sans-serif"
                    }} data-id="element-694">
                          {step.title}
                        </h4>
                        <p className="text-base text-slate-600 leading-relaxed max-w-2xl" style={{
                      fontFamily: 'system-ui, -apple-system, sans-serif'
                    }} data-id="element-695">
                          {step.desc}
                        </p>
                      </div>
                    </div>)}
                </div>
              </motion.div>

              {/* Section C: Solution */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-696">
                <div className="flex items-center gap-3 mb-4" data-id="element-697">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#003071'
                }} data-id="element-698">
                    04
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#003071'
                }} aria-hidden="true" data-id="element-699" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#003071] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-700">
                  Solution: AI parses, humans verify
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-6 sm:mb-8" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-701">
                  I designed a Claude-powered pipeline that ingests a CV,
                  extracts structured fields (publications, grants, courses,
                  dates) and surfaces them in a single review panel. Admins
                  scan, edit inline, and push to the faculty database in one
                  motion.
                </p>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-10" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-702">
                  The key design call: keep the human in the loop on every
                  field. Trust compounds; black-box automation breaks the moment
                  it&rsquo;s wrong once.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-10" data-id="element-703">
                  <ProofImage src="https://uploadthingy.s3.us-west-1.amazonaws.com/5KpMMuX7A152SY5E7yEEkP/Ideation_1.png" alt="Navigation iteration 1" caption="Iteration 01" data-id="element-704" />
                  <ProofImage src="https://uploadthingy.s3.us-west-1.amazonaws.com/fZ3J5Y9Kes9FYqDKGquNRG/Ideation_2.png" alt="Navigation iteration 2" caption="Iteration 02" data-id="element-705" />
                  <ProofImage src="https://uploadthingy.s3.us-west-1.amazonaws.com/sAd42ZLH2mUmRLh4anJCPC/Ideation_3.png" alt="Navigation iteration 3" caption="Iteration 03 (final)" data-id="element-706" />
                </div>

                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-10" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-707">
                  And here&rsquo;s the shipped home view:
                </p>

                <ProofImage src="https://cdn.magicpatterns.com/uploads/b1CFgyJEco99E9Ys6agrnZ/Home_Page_FIS.png" alt="Faculty Information System home page interface" caption="Shipped: FIS home page" data-id="element-708" />
              </motion.div>

              {/* Engineering collaboration */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-709">
                <div className="flex items-center gap-3 mb-4" data-id="element-710">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#003071'
                }} data-id="element-711">
                    05
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#003071'
                }} aria-hidden="true" data-id="element-712" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#003071] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-713">
                  Build: Design and engineering, same table
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-10" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-714">
                  I ran live working sessions with Python engineers and
                  stakeholders, translating technical constraints into design
                  opportunities in the room. Faster than handoffs. Less rework.
                </p>

                <VimeoEmbed videoId="1142229645" caption="Collaborative design sessions with engineers and stakeholders" data-id="element-715" />
              </motion.div>

              {/* Live prototype demo */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-716">
                <div className="flex items-center gap-3 mb-4" data-id="element-717">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#003071'
                }} data-id="element-718">
                    06
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#003071'
                }} aria-hidden="true" data-id="element-719" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#003071] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-720">
                  Demo: The working prototype
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-10" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-721">
                  Watch a 36-page CV get parsed in seconds. Publications,
                  grants, courses, and dates, all into editable fields ready for
                  the faculty database.
                </p>

                <VimeoEmbed videoId="1148647481" caption="AI-powered CV parsing, end to end" aspect={52.31} data-id="element-722" />

                {/* Designer's note on the demo */}
                <div className="bg-slate-50 rounded-2xl border border-slate-100 p-6 sm:p-10 mt-10" data-id="element-723">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#003071] mb-4" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-724">
                    A note on the demo
                  </div>
                  <h4 className="text-xl sm:text-2xl font-light text-slate-900 mb-5 leading-snug" style={{
                  fontFamily: "'Gefika', sans-serif"
                }} data-id="element-725">
                    The polish you&rsquo;re seeing isn&rsquo;t the final
                    product, and that was on purpose.
                  </h4>
                  <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-5" style={{
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }} data-id="element-726">
                    Going in, we were pulling components from four different UI
                    libraries: buttons, inputs, and patterns rendering in
                    slightly different styles across the platform. By the time
                    we filmed this walkthrough, we had consolidated down to a
                    single library, but we hadn&rsquo;t yet refactored every
                    screen to match.
                  </p>
                  <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-5" style={{
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }} data-id="element-727">
                    That was a deliberate trade-off. With a platform this size
                    and a timeline this tight, the call was to ship function
                    first and unify visual consistency in a dedicated pass. The
                    full design system did get produced. I handed off the style
                    guide and consolidated component set when my engagement
                    ended. I wasn&rsquo;t there to see it fully implemented, and
                    I don&rsquo;t have access to the final shipped version.
                  </p>
                  <p className="text-base sm:text-lg text-slate-700 leading-relaxed" style={{
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }} data-id="element-728">
                    It&rsquo;s the kind of compromise that comes up on every
                    real project: when speed, scope, and polish are competing,
                    you pick which one moves second. On this one, function moved
                    first, and the system caught up after.
                  </p>
                </div>
              </motion.div>

              {/* Section D: Outcome */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-729">
                <div className="flex items-center gap-3 mb-4" data-id="element-730">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#003071'
                }} data-id="element-731">
                    07
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#003071'
                }} aria-hidden="true" data-id="element-732" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#003071] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-733">
                  Outcome: Shipped and measured
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 py-8 mb-8 border-y border-slate-100" data-id="element-734">
                  <div data-id="element-735">
                    <div className="text-4xl sm:text-5xl font-light text-[#003071] mb-2" style={{
                    fontFamily: "'Gefika', sans-serif"
                  }} data-id="element-736">
                      85%
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-[#003071]" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-737">
                      First-pass parsing accuracy
                    </div>
                  </div>
                  <div data-id="element-738">
                    <div className="text-4xl sm:text-5xl font-light text-[#003071] mb-2" style={{
                    fontFamily: "'Gefika', sans-serif"
                  }} data-id="element-739">
                      75%
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-[#003071]" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-740">
                      Reduction in admin review time
                    </div>
                  </div>
                  <div data-id="element-741">
                    <div className="text-4xl sm:text-5xl font-light text-[#003071] mb-2" style={{
                    fontFamily: "'Gefika', sans-serif"
                  }} data-id="element-742">
                      100%
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-[#003071]" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-743">
                      Positive UX rating in pilot testing
                    </div>
                  </div>
                </div>

                <p className="text-base sm:text-lg text-slate-700 leading-relaxed" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-744">
                  The pilot proved the design can scale beyond a single
                  university. We secured a 6-month Amazon pilot for 2026
                  rollout, with the methodology being prepped for an Amazon
                  research white paper.
                </p>
              </motion.div>
            </section>
          </main>
        </div>
      </div>

      <FloatingButtons data-id="element-745" />
      <ChatDrawer data-id="element-746" />
      <MobileBottomCTA data-id="element-747" />
      {!isOverlay && <Footer data-id="element-748" />}
    </>;
}